import {Injectable} from '@angular/core';
import * as _ from 'lodash';
import {
    ScheduleIntervalRange,
    ScheduleIntervalsData,
    ScheduleInterval,
    ScheduleIntervalsWeekDay,
    ScheduleIntervalType,
    ScheduleIntervalsDayData, MenuElementType120
} from "../../models";
import {ControlService} from "./control.service";
import {IntervalHourPipe} from "../../shared";
import {AppAlertService} from "../app-alert.service";
import {SubtitlesService} from "../subtitles.service";

@Injectable({
    providedIn: 'root'
})
export class ScheduleIntervalsService {
    currentDayIndex: number = -1;
    currentIndex: number = -1;
    currentItem: ScheduleInterval = {
        start: -1,
        end: -1,
    };
    currentRangeIndex: number = -1;
    hours: number[] = [];
    intervals: ScheduleInterval[] = [];
    showRange = false;
    showWeekDays = false;
    weekDays: ScheduleIntervalsWeekDay[] = [];

    constructor(
        private appAlert: AppAlertService,
        private control: ControlService,
        private intervalHour: IntervalHourPipe,
        private subtitles: SubtitlesService,
    ) {
    }

    setDay(day: ScheduleInterval[], currentDayIndex: number): void {
        this.hours = new Array(24);
        this.showRange = false;
        this.currentDayIndex = currentDayIndex;

        this.weekDays = [];
        for (let i = 0; i < 7; i++) {
            this.weekDays.push({
                selected: i === currentDayIndex
            });
        }

        this.intervals = [];
        for (let i = 0; i < day.length; i++) {
            this.intervals.push(day[i]);
        }
    }

    setRange(index: number): void {
        this.showRange = true;
        this.currentRangeIndex = index;

        const elementVal = this.control.elementVal.getValue() as MenuElementType120;
        if (elementVal.params.days[this.currentDayIndex][index].start < 0
            || elementVal.params.days[this.currentDayIndex][index].end < 0) {
            const maxTime = Math.max.apply(Math,
                elementVal.params.days[this.currentDayIndex].map(x => x.end));

            let maxHour = parseInt(this.intervalHour.transform(maxTime), 10);
            if (!isNaN(maxHour)) {
                maxHour = maxHour >= 23 ? -1 : maxHour;
            }

            this.currentItem = {
                start: isNaN(maxHour) ? 0 : (maxHour + 1) * 60,
                end: isNaN(maxHour) ? 10 : (maxHour + 1) * 60 + 10
            }
        } else {
            this.currentItem = _.cloneDeep(elementVal.params.days[this.currentDayIndex][index]);
        }
    }

    saveRange(): void {
        const elementVal = this.control.elementVal.getValue() as MenuElementType120;
        elementVal.params.days[this.currentDayIndex][this.currentRangeIndex] = this.currentItem;
        this.control.elementVal.next(elementVal);
        this.setDay(elementVal.params.days[this.currentDayIndex], this.currentDayIndex);
        this.currentRangeIndex = -1;
        this.showRange = false;
    }

    cancelRange(): void {
        this.showRange = false;
        this.currentRangeIndex = -1;
    }

    increaseValue(range: ScheduleIntervalRange, type: ScheduleIntervalType): void {
        const value = type == 'hour' ? 60 : 10;

        if (range === 'start') {
            this.currentItem.start = this.currentItem.start + value <= 1430
                ? this.currentItem.start + value
                : this.currentItem.start;
        } else {
            this.currentItem.end = this.currentItem.end + value <= 1440
                ? this.currentItem.end + value
                : this.currentItem.end;
        }
        this.currentItem.end = this.currentItem.start >= this.currentItem.end
            ? this.currentItem.start + 10
            : this.currentItem.end;
    }

    reduceValue(range: ScheduleIntervalRange, type: ScheduleIntervalType): void {
        const value = type == 'hour' ? 60 : 10;

        if (range === 'end') {
            this.currentItem.end = this.currentItem.end - value >= 10
                ? this.currentItem.end - value
                : this.currentItem.end;
        } else {
            this.currentItem.start = this.currentItem.start - value >= 0
                ? this.currentItem.start - value
                : this.currentItem.start;
        }

        this.currentItem.start = this.currentItem.end <= this.currentItem.start
            ? this.currentItem.end - 10
            : this.currentItem.start;
    }

    copyDays(): void {
        for (let i = 0; i < 7; i++) {
            if (this.weekDays[i].selected && i !== this.currentDayIndex) {
                const elementVal = this.control.elementVal.getValue() as MenuElementType120;
                elementVal.params.days[i] = _.cloneDeep(elementVal.params.days[this.currentDayIndex]);
                this.weekDays[i].selected = false;
                this.control.elementVal.next(elementVal);
            }
            this.showWeekDays = false;
        }
    }

    cancelDays() {
        for (let i = 0; i < 7; i++) {
            this.weekDays[i].selected = i === this.currentDayIndex;
        }
        this.showWeekDays = false;
    }

    clearRangeAlert(intervalIndex: number): void {
        this.currentIndex = intervalIndex;
        const message = `<div class='text-align-center'>${this.subtitles.setSubtitle(4379)}</div>`;

        this.appAlert.showAppAlert(message, this.subtitles.setSubtitle(475),
            this.clearRange.bind(this), this.subtitles.setSubtitle(71), null);
    }

    private clearRange(): void {
        const elementVal = this.control.elementVal.getValue() as MenuElementType120;
        elementVal.params.days[this.currentDayIndex][this.currentIndex] = {
            start: -1,
            end: -1
        };
        this.control.elementVal.next(elementVal);
        this.setDay(elementVal.params.days[this.currentDayIndex], this.currentDayIndex);
    }

    returnSchedule(): void {
        const scheduleData: ScheduleIntervalsData = {
            schedule: [],
        };

        const elementVal = this.control.elementVal.getValue() as MenuElementType120;
        elementVal.params.days.forEach((val: ScheduleInterval[], index: number) => {
            const dayData: ScheduleIntervalsDayData = {
                day: index,
                time: [],
            }

            val.sort((a, b) => {
                const x = a.start < 0 ? 9999 : a.start;
                const y = b.start < 0 ? 9999 : b.start;
                return x < y ? -1 : x > y ? 1 : 0;
            });

            val.forEach(timeValue => {
                if (timeValue.start !== -1 && timeValue.end !== -1) {
                    dayData.time.push({
                        start: timeValue.start,
                        end: timeValue.end
                    })
                }
            })

            scheduleData.schedule.push(dayData);
        });

        this.control.returnVal(scheduleData);
    }
}
